import { useState } from "react";
import { Copy, Loader2, Mail, MessageSquareText } from "lucide-react";

import type { UseConsumerReferralState } from "../../hooks/useConsumerReferral";
import {
  buildMailtoHref,
  buildSmsHref,
  getReferralShareMessage,
  getReferralShareSubject,
  type ReferralIdentity,
} from "../../lib/dashboard-settings";
import type { ReferralShareAction } from "../../lib/referral-analytics";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

/**
 * The ONE implementation of the referral share controls — link field +
 * Copy / Text / Email — shared by the Settings referral card, the dashboard
 * banner, the post-publish prompt, and the waitlist-card share sheet.
 *
 * It renders whatever `referral` (the shared-store hook state) says: the real
 * minted URL, an honest minting state, or a truthful "unavailable — try again"
 * with a retry, and NEVER a fabricated link. `onAction` reports the action
 * name only, so a caller can emit analytics without ever seeing the URL.
 */
export interface ReferralShareActionsProps {
  referral: UseConsumerReferralState;
  identity: ReferralIdentity;
  onAction?: (action: ReferralShareAction) => void;
  /**
   * Render the inline "couldn't create your link — Try again" row on error.
   * The Settings card owns a fuller error block in its tracking section, so
   * it turns this off to avoid two retry buttons for one failure.
   */
  showRetry?: boolean;
  /**
   * "row" (default) lays the link + three actions on one line at `lg`;
   * "compact" keeps the link on its own row with the actions beneath — for
   * narrow containers (the share sheet dialog) where a four-column row
   * squeezes the URL field to a few characters.
   */
  layout?: "row" | "compact";
  className?: string;
}

async function copyTextToClipboard(text: string) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

const LINK_ACTION_READY =
  "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold text-foreground transition-[color,background-color,scale] duration-200 hover:bg-primary/5 hover:text-primary active:scale-[0.96]";
const LINK_ACTION_DISABLED =
  "pointer-events-none inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold text-muted-foreground opacity-50";

export function ReferralShareActions({
  referral,
  identity,
  onAction,
  showRetry = true,
  layout = "row",
  className,
}: ReferralShareActionsProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  const shareSubject = getReferralShareSubject(identity);
  const shareMessage = referral.shareUrl
    ? getReferralShareMessage(identity, referral.shareUrl)
    : null;
  const smsHref = shareMessage ? buildSmsHref(shareMessage) : null;
  const mailtoHref = shareMessage
    ? buildMailtoHref(shareSubject, shareMessage)
    : null;
  const linkReady = Boolean(referral.shareUrl) && !referral.isLoading;
  const linkFieldPlaceholder = referral.error
    ? "Link unavailable — try again"
    : "Creating your link…";

  const handleCopy = async () => {
    if (!referral.shareUrl) return;
    await copyTextToClipboard(referral.shareUrl);
    setCopyState("copied");
    onAction?.("copy");
  };

  return (
    <div className={className}>
      <div
        data-testid="referral-share-actions"
        data-layout={layout}
        className={cn(
          // Base (every layout, every viewport): the link on its own row,
          // the three actions in one row beneath — never three stacked
          // full-width buttons on a phone.
          "grid grid-cols-3 gap-2 rounded-2xl border border-border bg-background p-1 [&>input]:col-span-3",
          layout === "row" &&
            "lg:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6.5rem] lg:items-center lg:[&>input]:col-span-1",
        )}
      >
        <input
          readOnly
          value={referral.shareUrl ?? ""}
          placeholder={linkReady ? undefined : linkFieldPlaceholder}
          disabled={!linkReady}
          aria-label="Referral link"
          data-testid="referral-link-field"
          onFocus={(event) => event.currentTarget.select()}
          className="min-h-11 min-w-0 overflow-x-auto rounded-xl bg-transparent px-3 text-sm text-foreground outline-none disabled:text-muted-foreground"
        />
        <Button
          type="button"
          variant="ghost"
          onClick={handleCopy}
          disabled={!linkReady}
          aria-label="Copy referral link"
          className="min-h-11 rounded-xl text-primary hover:bg-primary/10"
        >
          {referral.isLoading ? (
            <Loader2
              className="h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copyState === "copied" ? "Copied" : "Copy"}
        </Button>
        <a
          href={smsHref ?? undefined}
          aria-disabled={!linkReady}
          onClick={(event) => {
            if (!linkReady) {
              event.preventDefault();
              return;
            }
            onAction?.("text");
          }}
          className={cn(linkReady ? LINK_ACTION_READY : LINK_ACTION_DISABLED)}
        >
          <MessageSquareText className="h-4 w-4" aria-hidden="true" />
          Text
        </a>
        <a
          href={mailtoHref ?? undefined}
          aria-disabled={!linkReady}
          onClick={(event) => {
            if (!linkReady) {
              event.preventDefault();
              return;
            }
            onAction?.("email");
          }}
          className={cn(linkReady ? LINK_ACTION_READY : LINK_ACTION_DISABLED)}
        >
          <Mail className="h-4 w-4" aria-hidden="true" />
          Email
        </a>
      </div>
      {showRetry && referral.error ? (
        <div className="mt-2 flex items-center gap-3 px-1">
          <p className="text-sm text-muted-foreground">
            We couldn&apos;t create your link.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={referral.retry}
            data-testid="referral-share-retry"
          >
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}
